import React from "react";
import { AriaButtonProps, useButton, useToggleButton } from "react-aria";
import { cx, mergeRefs } from "../../utils/dom";
import { Loader } from "../loader";
import { useButtonGroup } from "./button-group";
import { useSpace } from "../space/space";
import { ToggleState, useToggleState } from "react-stately";
import {
  BorderRadius,
  ButtonAppearance,
  ButtonSize,
  Color,
  ColorVariant,
} from "../../utils/theme";
import { useTheme } from "../theme";
import { ResponsiveProp, StyleProps } from "../../utils/types";
import { breakpointClassNames } from "../../utils/helpers";
import { ToggleButtonProps } from "react-aria-components";

export interface ButtonToggleProps {
  isToggle?: boolean;
  idleColor?: Color;
  idleVariant?: ColorVariant;
  activeColor?: Color;
  activeVariant?: ColorVariant;
}

export type ButtonProps = StyleProps<"wrapper" | "loader"> &
  AriaButtonProps<"button" | "div" | "a"> &
  ButtonToggleProps & {
    appearance?: ButtonAppearance;
    append?: React.ReactNode;
    as?: React.ElementType;
    color?: Color;
    defaultSelected?: boolean;
    form?: string;
    isIconButton?: boolean;
    isLoading?: boolean;
    isSelected?: boolean;
    prepend?: React.ReactNode;
    radius?: BorderRadius;
    role?: string;
    size?: ResponsiveProp<ButtonSize>;
    variant?: ColorVariant;
  } & Pick<ToggleButtonProps, "onChange">;

/*
    TODO:
    - [ ] Support onClick prop
    - [ ] Standardize props with universal button props
  */

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const space = useSpace();
    const buttonGroupProps = useButtonGroup();
    const theme = useTheme();

    const {
      color = theme?.color ?? "violet",
      variant = "solid",
      append,
      prepend,
      size = "md",
      children,
      as = "button",
      className,
      isLoading,
      radius = "full",
      isToggle,
      activeColor = theme?.color,
      activeVariant = "solid",
      idleColor = "gray",
      idleVariant = "outline",
      isIconButton,
      style,
      classNames,
      styles,
      form,
      ...otherProps
    } = { ...buttonGroupProps, ...props } as ButtonProps;

    const state = isToggle
      ? useToggleState({
          ...otherProps,
        })
      : ({} as ToggleState);

    const buttonRef = React.useRef(null);
    const { buttonProps, isPressed } = isToggle
      ? useToggleButton({ ...otherProps, elementType: as }, state, buttonRef)
      : useButton({ ...otherProps, elementType: as }, buttonRef);

    function getColor() {
      if (!state.isSelected && isToggle) {
        return { color: idleColor, variant: idleVariant };
      }
      if (state.isSelected && isToggle) {
        return { color: activeColor, variant: activeVariant };
      }

      return { variant, color };
    }

    const Comp = as;

    return (
      <Comp
        style={{ ...style, ...styles?.wrapper }}
        data-is-toggle={isToggle}
        data-pressed={isPressed}
        data-radius={radius}
        data-icon-button={isIconButton}
        data-loading={isLoading}
        aria-busy={isLoading}
        data-color={getColor().color}
        data-variant={getColor().variant}
        data-selected={state.isSelected}
        className={cx(
          breakpointClassNames("size", size),
          className,
          "p-button",
          space.classNames
        )}
        ref={mergeRefs(ref, buttonRef)}
        form={form}
        {...(buttonProps as Omit<typeof buttonProps, "color">)}
      >
        {prepend}

        {children}

        {isLoading ? (
          <Loader
            className={cx(["p-button-loader", classNames?.loader])}
            style={{ marginLeft: 8, ...styles?.loader }}
            color="inherit"
            size={"xs"}
          />
        ) : (
          append
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";
